import React, { useEffect, useRef, useState } from 'react';
import { Flame, Wine, Beer, GlassWater, UtensilsCrossed, Sun, Clock, FileText } from 'lucide-react';
import { happyHourSpecials, restaurantInfo } from '../config/siteConfig';

const dealIcons = {
  wine: Wine,
  beer: Beer,
  cocktail: GlassWater,
  food: UtensilsCrossed,
};

const MenuSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Get current month name
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="menu" ref={sectionRef} className="py-20 lg:py-28 texture-wood text-[#FDF6E9] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4A574] to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#D4A574] font-semibold tracking-widest text-sm uppercase">What's Cookin'</span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FDF6E9] tracking-wide leading-[0.95] mt-3">
            THE MENU
          </h2>
          <div className="divider-ornament max-w-xs mx-auto mt-4">
            <span className="text-2xl">🍔</span>
          </div>
        </div>

        {/* Happy Hour - Chalkboard style */}
        <div className={`chalkboard rounded-lg p-8 sm:p-10 shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>

          {/* Title */}
          <div className="flex items-center justify-center gap-3 mb-3">
            <Flame className="w-6 h-6 text-[#D4A574]" />
            <h3 className="font-display text-3xl text-[#FDF6E9] tracking-wide">{happyHourSpecials.title}</h3>
          </div>

          {/* Time badge */}
          <div className="flex items-center justify-center gap-2 mb-10">
            <Clock className="w-4 h-4 text-[#C9A227]" />
            <span className="text-[#C9A227] font-semibold tracking-wider text-sm uppercase">
              Daily {happyHourSpecials.time}
            </span>
          </div>

          {/* Deals Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            {happyHourSpecials.deals.map((deal, index) => {
              const Icon = dealIcons[deal.icon] || Wine;
              return (
                <div
                  key={index}
                  className="group relative bg-[#1e2b20] border border-[#C9A227]/20 rounded-lg p-5 text-center
                             hover:border-[#C9A227]/50 hover:bg-[#243228] transition-all duration-300 cursor-default"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Icon className="w-6 h-6 text-[#C9A227] mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
                  <div className="font-display text-3xl sm:text-4xl text-[#D4A574] tracking-wide leading-none mb-1">
                    {deal.price}
                  </div>
                  <div className="text-[#FDF6E9]/70 text-sm font-light mt-1">
                    {deal.item}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sunday Special Callout */}
          <div className="flex justify-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border border-[#C9A227]/30 rounded-full bg-[#C9A227]/10 px-5 sm:px-6 py-2.5">
              <Sun className="w-5 h-5 text-[#C9A227] shrink-0" />
              <span className="font-display text-lg sm:text-xl text-[#D4A574] tracking-wide">
                {happyHourSpecials.sunday.label}
              </span>
              <span className="hidden sm:inline-block w-px h-4 bg-[#C9A227]/40" />
              <span className="text-[#FDF6E9]/60 text-sm font-light">
                {happyHourSpecials.sunday.description}
              </span>
            </div>
          </div>

          {/* Bottom badge */}
          <div className="mt-10 text-center">
            <div className="inline-block stamp text-[#D4A574] rotate-2">
              Kitchen Open Daily
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className={`mt-10 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a href={restaurantInfo.fullMenuPdfUrl} target="_blank" rel="noopener noreferrer" className="px-6 py-4 bg-transparent border-2 border-[#D4A574] text-[#D4A574] font-display text-xl tracking-wide hover:bg-[#D4A574] hover:text-[#1a1612] transition-colors duration-300 flex items-center justify-center gap-2">
              <FileText className="w-5 h-5 shrink-0" />
              FULL MENU (PDF)<span className="sr-only"> — opens a PDF in a new tab</span>
            </a>
            <a href={restaurantInfo.drinkSpecialsUrl} target="_blank" rel="noopener noreferrer" className="px-6 py-4 bg-transparent border-2 border-[#D4A574] text-[#D4A574] font-display text-xl tracking-wide hover:bg-[#D4A574] hover:text-[#1a1612] transition-colors duration-300 flex items-center justify-center gap-2">
              <Wine className="w-5 h-5 shrink-0" />
              {currentMonth.toUpperCase()} DRINK SPECIALS<span className="sr-only"> — opens a PDF in a new tab</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
